import { type ClassValue, clsx } from "clsx"
import { twMerge } from "tailwind-merge"
import { parsePhoneNumberFromString } from "libphonenumber-js"
import { CommerceProduct } from "@/app/types"

enum ExperienceLevel {
  "SENIOR" = "senior",
  "JUNIOR" = "junior",
  "MID-SENIOR" = "mid-senior",
  "EXPERT" = "expert",
}

export const experienceLevelToRange = (level: ExperienceLevel | string): string => {
  switch (level) {
    case ExperienceLevel.JUNIOR:
      return "0-3 years"
    case ExperienceLevel["MID-SENIOR"]:
      return "3-5 years"
    case ExperienceLevel.SENIOR:
      return "5-8 years"
    case ExperienceLevel.EXPERT:
      return "8+ years"
    default:
      return "Not specified" // In case there’s an unhandled level
  }
}

export const stripHtml = (html: string) => html.replace(/<[^>]*>/g, "").trim()

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

export function getBaseURL() {
  return `${window.location.origin.toString()}`
}

export const convertFileToUrl = (file: File) => URL.createObjectURL(file)

export const NEXT_MONTH = new Date()
NEXT_MONTH.setMonth(NEXT_MONTH.getMonth() + 1)

export const phoneRegex = /^\+1\d{10}$/

/**
 * Formats and validates a US/Canada phone number.
 * @param value - raw input phone number
 * @returns string - E.164 formatted phone number (+1XXXXXXXXXX)
 * @throws Error if number is invalid
 */
export const formatUSPhone = (value: unknown): string | undefined => {
  if (typeof value !== "string") return undefined

  const raw = value.trim()
  if (!raw) return undefined

  const digits = raw.replace(/\D/g, "")

  let formatted: string | undefined

  if (raw.startsWith("+")) {
    formatted = "+" + digits
  } else if (digits.length === 10) {
    formatted = "+1" + digits
  } else if (digits.length === 11 && digits.startsWith("1")) {
    formatted = "+" + digits
  }

  if (!formatted) return undefined

  const phone = parsePhoneNumberFromString(formatted, "US")
  if (!phone?.isValid()) return undefined

  return phone.number
}

export const passRegx = /((?=.*\d)|(?=.*\W+))(?![.\n])(?=.*[A-Z])(?=.*[a-z]).*$/

export function formatDateString(dateString: string) {
  const options: Intl.DateTimeFormatOptions = {
    year: "numeric",
    month: "short",
    day: "numeric",
  }

  const date = new Date(dateString)
  const formattedDate = date.toLocaleDateString("en-US", options)

  return formattedDate
}

//change date format to 'ago' date format
export const multiFormatDateString = (timestamp: string = ""): string => {
  const date = new Date(timestamp)
  if (isNaN(date.getTime())) return "Just now" // Handle invalid date

  const now = new Date()
  const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000)

  const rtf = new Intl.RelativeTimeFormat("en", { numeric: "auto" })

  /**
   * Array of objects containing unit and seconds
   * used for calculating the difference between two dates
   * in a human-readable format.
   *
   * The units are in descending order, so the function
   * can stop at the first unit that is greater than or
   * equal to the difference in seconds.
   *
   * The values are based on the following assumptions:
   * - 1 year = 365 days
   * - 1 month = 30 days
   * - 1 day = 24 hours
   * - 1 hour = 60 minutes
   * - 1 minute = 60 seconds
   */
  const timeMappings: { unit: Intl.RelativeTimeFormatUnit; seconds: number }[] = [
    { unit: "year", seconds: 31536000 }, // 365 days
    { unit: "month", seconds: 2592000 }, // 30 days
    { unit: "day", seconds: 86400 },
    { unit: "hour", seconds: 3600 },
    { unit: "minute", seconds: 60 },
  ]

  for (const { unit, seconds } of timeMappings) {
    const diff = Math.floor(diffInSeconds / seconds)
    if (diff >= 1) return rtf.format(-diff, unit)
  }

  return "Just now"
}

export const getDateString = () => {
  const now: Date = new Date()
  const months = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ]
  return `${months[now.getMonth()]} ${now.getDate()}, ${now.getFullYear()}`
}

export function formatDate(timestamp: string | number | Date): string {
  const date = new Date(timestamp)
  if (isNaN(date.getTime())) return "Invalid date"

  return date.toLocaleDateString("en-US", {
    month: "long",
    day: "2-digit",
    year: "numeric",
  })
}

export const shorterDate = (date: Date | string | number): string => {
  return new Date(date).toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
  })
}

export function makePageTitle(collection: any, query: string = "") {
  if (collection) return `${collection.title}`
  return query.length ? `${query}` : "All"
}

export type CurrencyType = "USD" | "EUR" | "GBP"

export function mapCurrencyToSign(currency: CurrencyType) {
  switch (currency) {
    case "USD":
      return "$"
    case "EUR":
      return "€"
    case "GBP":
      return "£"

    default:
      return currency
  }
}

export function slugToName(slug: string) {
  return slug
    .split("-")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ")
}

export function nameToSlug(name: string) {
  return name.toLowerCase().split(" ").join("-")
}

export function makeBreadcrumbs(product: CommerceProduct, locale: string = "en") {
  return locale === "am" ? [{
    name: "መነሻ", href: "/"
  },
  { name: "ይግዙ", href: "/shop" },
  { name: product.name }
  ] :
    [
      { name: "Home", href: "/" },
      {
        name: "Products",
        href: "/shop",
      },
      { name: product.name },
    ]
}

export function getLocation(): Promise<{ latitude: number; longitude: number }> {
  return new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
      reject(new Error("Geolocation is not supported by your browser."))
    } else {
      navigator.geolocation.getCurrentPosition(
        (currentPosition) => {
          const position = {
            latitude: currentPosition.coords.latitude,
            longitude: currentPosition.coords.longitude,
          }
          resolve(position)
        },
        (error) => {
          reject(new Error("Unable to retrieve location: " + error.message))
        },
        {
          enableHighAccuracy: true,
          timeout: 5000, // Timeout in milliseconds
          maximumAge: 0, // Do not cache the position
        }
      )
    }
  })
}

// Helper to check if value is numeric
export const isNumericPrice = (value: any): boolean => {
  if (value === null || value === undefined || value === "") return false
  if (typeof value === "number") return !isNaN(value)
  if (typeof value === "string") {
    const trimmed = value.trim()
    return !isNaN(Number(trimmed)) && trimmed !== ""
  }
  return false
}

// Format numeric prices only (internal use)
export const formatNumericPrice = (value: number): string => {
  return value
    .toLocaleString("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })
}

// Smart price formatter - handles both numeric and alphanumeric
export const formatPrice = (value: number | string | null | undefined): string => {
  if (value === null || value === undefined) return ""
  
  // If it's a number or numeric string, format it
  if (isNumericPrice(value)) {
    const numValue = typeof value === "string" ? parseFloat(value) : value
    return formatNumericPrice(numValue)
  }
  
  // If it's alphanumeric (like "15/hr", "negotiable"), return as-is
  return String(value)
}

// Display price with currency symbol (for UI components)
export const formatDisplayPrice = (
  value: number | string | null | undefined,
  options?: { showCurrency?: boolean; fallback?: string }
): string => {
  const { showCurrency = true, fallback = "" } = options || {}
  
  if (value === null || value === undefined) return fallback
  
  const formatted = formatPrice(value)
  if (!formatted) return fallback
  
  // Only add $ for numeric values
  if (showCurrency && isNumericPrice(value)) {
    return `$${formatted}`
  }
  
  return formatted
}
